import * as React from 'react';
import {
  Button,
  Image,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  shorthands,
  tokens,
  Tooltip,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import { MoreVerticalRegular, SettingsRegular } from '@fluentui/react-icons';
import { useMediaQuery } from '@react-hook/media-query';
import type { MessageDescriptor } from 'react-intl';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import MicrosoftMonoChromeLogoSmall from '../media/logos/Microsoft-logo-wht-sm.svg';
import { openUrl } from '../modules/linking';
import { useFeatureFlagEnabled } from '../store';
import { FoundersHubUserOverlayDrawer } from './FoundersHubUserOverlayDrawer';
import { UserMenu } from './UserMenu';
import { WaffleMenu } from './WaffleMenu/WaffleMenu';

interface Option {
  title: MessageDescriptor;
  icon: React.ReactElement;
  path?: string;
  tooltip?: MessageDescriptor;
  onClick?: () => void;
}

export const NavOptions: Array<Option> = [
  {
    title: defineMessage({ defaultMessage: 'Settings', description: 'Title for link to Settings page' }),
    icon: <SettingsRegular />,
    path: '/settings',
  },
];

interface Props {
  options?: Array<Option>;
}

export function Header({ options = NavOptions }: Props) {
  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const isSmall = useMediaQuery('screen and (max-width: 640px)');
  const restoreFocusTargetAttribute = useRestoreFocusTarget();
  const waffleEnabled = useFeatureFlagEnabled('cloud_portal_waffle');

  return (
    <header role="banner" className={classes.root}>
      <div className={classes.logoContainer}>
        {waffleEnabled ? (
          <WaffleMenu />
        ) : (
          <Image
            className={classes.logo}
            src={MicrosoftMonoChromeLogoSmall}
            alt="Microsoft Logo"
            aria-label="Microsoft Logo"
          />
        )}
        <div className={classes.logoTextContainer}>
          <a href="/" className={classes.logoText}>
            Microsoft for Startups Founders Hub
          </a>
        </div>
      </div>
      <div className={classes.options}>
        {isSmall ? (
          <Menu hasIcons>
            <MenuTrigger>
              <Button appearance="primary" icon={<MoreVerticalRegular />} aria-label="More options" />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {options.map(({ icon, path, title, onClick }, i) => (
                  <MenuItem
                    key={i}
                    icon={icon}
                    onClick={() => {
                      if (onClick != null) {
                        onClick();
                        return;
                      }

                      path?.startsWith('http') ? openUrl(path) : history.push(path as string);
                    }}
                  >
                    {intl.formatMessage(title)}
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : (
          options.map(({ icon, path, title, tooltip, onClick }, i) => {
            const button = (
              <Button
                key={i}
                appearance="primary"
                size="large"
                icon={icon}
                aria-label={intl.formatMessage(title)}
                {...restoreFocusTargetAttribute}
                onClick={() => {
                  if (onClick != null) {
                    onClick();
                    return;
                  }
                  path?.startsWith('http') ? openUrl(path) : history.push(path as string);
                }}
              />
            );
            if (tooltip) {
              return (
                <Tooltip
                  relationship="description"
                  content={intl.formatMessage(tooltip)}
                  positioning="below"
                  withArrow
                  key={i}
                >
                  {button}
                </Tooltip>
              );
            } else {
              return button;
            }
          })
        )}

        <UserMenu />
      </div>
      <FoundersHubUserOverlayDrawer />
    </header>
  );
}

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorBrandBackground,
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexGrow: 0,
    height: '48px',
    ...shorthands.overflow('hidden'),
    color: tokens.colorNeutralForegroundOnBrand,
    boxShadow: tokens.shadow4,
  },
  options: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
    height: '100%',
    alignItems: 'center',
    justifyItems: 'stretch',
    columnGutter: tokens.spacingHorizontalM,
  },
  logoTextContainer: {
    whiteSpace: 'nowrap',
    marginLeft: tokens.spacingHorizontalM,
    '@media screen and (min-width: 321px) and (max-width: 480px)': {
      width: '174px',
      ...shorthands.overflow('hidden'),
      textOverflow: 'ellipsis',
    },
    '@media screen and (max-width: 320px)': {
      width: '84px',
      ...shorthands.overflow('hidden'),
      textOverflow: 'ellipsis',
    },
  },
  logoText: {
    color: tokens.colorNeutralForegroundInverted,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    ':hover': {
      color: tokens.colorNeutralForegroundInverted,
      textDecoration: 'underline',
    },
    ...shorthands.textDecoration('none'),
  },
  logoContainer: {
    display: 'flex',
    ...shorthands.margin(
      tokens.spacingVerticalNone,
      tokens.spacingHorizontalM,
      tokens.spacingVerticalNone,
      tokens.spacingHorizontalM
    ),
  },
  logo: {
    width: '20px',
    height: '20px',
    color: tokens.colorNeutralForegroundInverted,
  },
});
